import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { asArray, asBoolean, asObject, asString, cast } from '@restless/sanitizers'
import { asBigNumber, asEthAddress } from '@restless/ethereum'
import { BigNumber } from 'ethers'

export interface Synth {
  id: string
  name: string
  borrowIndex: BigNumber
  collateral: string
  collateralAddress: string
  collateralLimit: BigNumber
  collateralRatio: BigNumber
  interestRate: BigNumber
  liquidationUserFee: BigNumber
  liquidationArcRatio: BigNumber
  oracle: string
  positionCollateralMinimum: BigNumber
  synthetic: string
  syntheticAddress: string
}

export interface Saving {
  id: string
  name: string
  symbol: string
  syntheticAddress: string
  paused: boolean
  indexLastUpdate: BigNumber
  arcFee: BigNumber
  savingsRate: BigNumber
  savingsIndex: BigNumber
  totalSupplied: BigNumber
}

export interface PositionWithSynth {
  id: string
  owner: string
  synth: string
  borrowedAmountValue: BigNumber
}

export class GraphService {
  constructor(private client: ApolloClient<NormalizedCacheObject>) {}

  async fetchSynths() {
    const _synthsData = await this.client.query({ query: GET_SYNTH })
    let synths: Synth[] = []

    try {
      synths = cast<Synth[]>(_synthsData.data?.synths, asArray(asSynth))
    } catch (err) {
      console.error(`Error fetching synths: ${err}`)
    }
    return synths
  }

  async fetchSavings() {
    const _savingsData = await this.client.query({
      query: GET_SAVING,
      fetchPolicy: 'network-only',
    })
    return cast<Saving[]>(_savingsData.data?.savings, asArray(asSaving))
  }

  async fetchHighestPositionOfSynths(account: string, stateContractAddresses: string[]) {
    if (!account || !stateContractAddresses || stateContractAddresses.length === 0) {
      return null
    }

    const _positionsData = await this.client.query({
      query: GET_TOP_POSITION_FOR_SYNTHS,
      variables: {
        account,
        synthAddresses: stateContractAddresses,
      },
      fetchPolicy: 'network-only',
    })
    const positionsArr = cast<PositionWithSynth[]>(
      _positionsData.data?.positions,
      asArray(asPosition),
    )

    return positionsArr[0]
  }
}

export const GET_SYNTH = gql`
  query Synth {
    synths(orderBy: id, orderDirection: desc) {
      id
      name
      syntheticAddress
      collateralLimit
      collateralAddress
      positionCollateralMinimum
      collateralRatio
      liquidationUserFee
      liquidationArcRatio
      collateral
      synthetic
      interestRate
      borrowIndex
      oracle
    }
  }
`

export const GET_SAVING = gql`
  query Saving {
    savings(orderBy: id, orderDirection: desc) {
      id
      name
      symbol
      syntheticAddress
      paused
      indexLastUpdate
      arcFee
      savingsRate
      savingsIndex
      totalSupplied
    }
  }
`

const GET_TOP_POSITION_FOR_SYNTHS = gql`
  query GetHighestPositionOfSynths($account: Bytes!, $synthAddresses: [Bytes!]) {
    positions(
      where: { owner_contains: $account, synth_in: $synthAddresses }
      orderBy: borrowedAmountValue
      orderDirection: desc
      first: 1
    ) {
      id
      owner
      synth
      borrowedAmountValue
    }
  }
`

const asSynth = asObject<Synth>({
  id: asEthAddress,
  name: asString,
  oracle: asEthAddress,
  syntheticAddress: asEthAddress,
  collateralAddress: asEthAddress,
  collateralRatio: asBigNumber,
  liquidationUserFee: asBigNumber,
  liquidationArcRatio: asBigNumber,
  collateralLimit: asBigNumber,
  positionCollateralMinimum: asBigNumber,
  collateral: asString,
  synthetic: asString,
  interestRate: asBigNumber,
  borrowIndex: asBigNumber,
})

const asSaving = asObject<Saving>({
  id: asEthAddress,
  name: asString,
  symbol: asString,
  syntheticAddress: asEthAddress,
  paused: asBoolean,
  indexLastUpdate: asBigNumber,
  arcFee: asBigNumber,
  savingsRate: asBigNumber,
  savingsIndex: asBigNumber,
  totalSupplied: asBigNumber,
})

const asPosition = asObject<PositionWithSynth>({
  id: asString,
  owner: asEthAddress,
  synth: asEthAddress,
  borrowedAmountValue: asBigNumber,
})
